import React, { useState, useEffect, useMemo } from 'react';
import { Property } from '@/types';
import { getPropertiesByLocationApi } from '@/services/adminPropertyApiClient';
import { SpinnerIcon, ExclamationTriangleIcon, Squares2X2Icon, MapPinIcon, FunnelIcon, XMarkIcon, ChevronLeftIcon, ChevronRightIcon } from '@/components/Icons';
import { NewDashboardHeader } from '@/components/NewDashboardHeader';
import { Footer } from '@/components/Footer';
import { PropertyCard } from '@/components/broker/PropertyCard';
import { PublicMapView } from '@/components/PublicMapView';
import { LocationFilters } from '@/components/public/LocationFilters';
import { useLocationPagination } from '@/hooks/useLocationPagination';
import { RankingModal } from '@/components/public/RankingModal';
import { ShareGroupModal } from '@/components/ShareGroupModal';
import pako from 'pako';

interface SharedLocationViewProps {
  city: string;
  locality: string;
}

export const SharedLocationView: React.FC<SharedLocationViewProps> = ({ city, locality }) => {
  const [allProperties, setAllProperties] = useState<(Property & { sourceCollection: string })[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'grid' | 'map'>('grid');
  
  const [priceRange, setPriceRange] = useState({ min: '', max: '' });
  const [selectedNeighborhoods, setSelectedNeighborhoods] = useState<string[]>([]);
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  // Selection/Share State
  const [likedPropertyIds, setLikedPropertyIds] = useState<string[]>([]);
  const [isRankingModalOpen, setIsRankingModalOpen] = useState(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [shareLoading, setShareLoading] = useState(false);
  const [sharedUrl, setSharedUrl] = useState<string | null>(null);
  const [shareError, setShareError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const fetched = await getPropertiesByLocationApi(city, locality);
        setAllProperties(fetched.map(p => ({ ...p, sourceCollection: 'ciencuadras' })));
      } catch (e) {
        setError("No se pudieron cargar los inmuebles.");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [city, locality]);

  const neighborhoods = useMemo(() => {
    const names = allProperties.map(p => p.neighborhoodName).filter((n): n is string => !!n);
    return Array.from(new Set(names)).sort();
  }, [allProperties]);

  const filteredProperties = useMemo(() => {
    return allProperties.filter(p => {
      const matchesNeighborhood = selectedNeighborhoods.length === 0 || (p.neighborhoodName && selectedNeighborhoods.includes(p.neighborhoodName));
      const minPrice = priceRange.min ? Number(priceRange.min) : 0;
      const maxPrice = priceRange.max ? Number(priceRange.max) : Infinity;
      return matchesNeighborhood && p.precio_venta >= minPrice && p.precio_venta <= maxPrice;
    });
  }, [allProperties, selectedNeighborhoods, priceRange]);

  const { paginatedItems, totalPages, currentPage, goToPage, resetPagination } = useLocationPagination(filteredProperties, 12);

  useEffect(() => { resetPagination(); }, [selectedNeighborhoods, priceRange]);

  const handleToggleLike = (id: string) => setLikedPropertyIds(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);

  const handleViewProperty = (property: Property) => {
      const source = (property as any).sourceCollection;
      const shortCode = source === 'brokers' ? 'b' : source === 'aliados' ? 'a' : 'c';
      window.open(`${window.location.origin}/share/${property.id}?collection=${shortCode}`, '_blank');
  };

  const handleGenerateSelectionLink = async (rankedIds: string[]) => {
    setShareLoading(true);
    setShareError(null);
    setIsShareModalOpen(true);
    try {
      const data = JSON.stringify(rankedIds);
      const compressed = pako.deflate(data);
      const base64 = btoa(String.fromCharCode.apply(null, Array.from(compressed)));
      const url = `${window.location.origin}/#/selection/${encodeURIComponent(base64)}`;
      setSharedUrl(url);
    } catch (e) {
      setShareError("Error al generar el enlace de selección.");
    } finally {
      setShareLoading(false);
    }
  };

  if (loading) return <div className="min-h-screen flex flex-col"><NewDashboardHeader /><main className="flex-grow flex items-center justify-center"><SpinnerIcon className="w-12 h-12 text-sky-600 animate-spin" /></main><Footer /></div>;

  const likedProperties = allProperties.filter(p => likedPropertyIds.includes(p.id));

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <NewDashboardHeader />
      <main className="flex-grow max-w-7xl mx-auto px-4 sm:px-6 py-6 w-full">
        <div className="mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">{locality}</h1>
          <p className="text-sm sm:text-base text-gray-500">{city}, Colombia • {filteredProperties.length} inmuebles activos</p>
        </div>

        <div className="flex flex-col lg:flex-row gap-6">
          <aside className="hidden lg:block w-72 flex-shrink-0">
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 sticky top-24">
              <LocationFilters 
                neighborhoods={neighborhoods}
                selectedNeighborhoods={selectedNeighborhoods}
                onNeighborhoodChange={setSelectedNeighborhoods}
                priceRange={priceRange}
                onPriceRangeChange={setPriceRange}
                onClear={() => { setPriceRange({ min: '', max: '' }); setSelectedNeighborhoods([]); }}
                resultsCount={filteredProperties.length}
              />
            </div>
          </aside>
          <div className="flex-grow">
            <div className="flex items-center justify-between mb-4">
              <button onClick={() => setIsFilterOpen(true)} className="lg:hidden flex items-center px-4 py-2 bg-white border rounded-lg text-sm font-medium shadow-sm"><FunnelIcon className="w-4 h-4 mr-2" /> Filtros</button>
              <div className="flex items-center bg-white rounded-lg shadow-sm border p-1 ml-auto">
                <button onClick={() => setViewMode('grid')} className={`p-2 rounded-md ${viewMode === 'grid' ? 'bg-sky-100 text-sky-600' : 'text-gray-400'}`}><Squares2X2Icon className="w-5 h-5" /></button>
                <button onClick={() => setViewMode('map')} className={`p-2 rounded-md ${viewMode === 'map' ? 'bg-sky-100 text-sky-600' : 'text-gray-400'}`}><MapPinIcon className="w-5 h-5" /></button>
              </div>
            </div>

            {filteredProperties.length === 0 ? <div className="bg-white p-12 rounded-2xl text-center shadow-sm border"><p className="text-gray-500">No hay inmuebles con estos filtros.</p></div> : viewMode === 'grid' ? (
              <div className="space-y-8">
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
                  {paginatedItems.map(p => <PropertyCard key={p.id} property={p} isSelected={false} onView={() => handleViewProperty(p)} onShare={() => {}} onToggleSelect={() => {}} isCompact={false} source={p.sourceCollection as any} appUser={null} onInterested={() => {}} isLiked={likedPropertyIds.includes(p.id)} onToggleLike={() => handleToggleLike(p.id)} hideSpecialStatus={true} />)}
                </div>
                {totalPages > 1 && (
                  <div className="flex items-center justify-center space-x-2 py-4">
                    <button onClick={() => goToPage(currentPage - 1)} disabled={currentPage === 1} className="p-2 rounded-lg border bg-white disabled:opacity-30"><ChevronLeftIcon className="w-5 h-5" /></button>
                    <span className="text-sm font-medium">Página {currentPage} de {totalPages}</span>
                    <button onClick={() => goToPage(currentPage + 1)} disabled={currentPage === totalPages} className="p-2 rounded-lg border bg-white disabled:opacity-30"><ChevronRightIcon className="w-5 h-5" /></button>
                  </div>
                )}
              </div>
            ) : <div className="h-[500px] sm:h-[600px] rounded-2xl overflow-hidden shadow-sm border"><PublicMapView properties={filteredProperties} onViewProperty={handleViewProperty} /></div>}
          </div>
        </div>
      </main>
      <Footer />

      {/* Floating Selection Button */}
      {likedPropertyIds.length > 0 && (
        <div className="fixed bottom-8 right-8 z-40 animate-bounce">
          <button 
            onClick={() => setIsRankingModalOpen(true)}
            className="flex items-center px-6 py-3 bg-sky-600 text-white rounded-full shadow-2xl hover:bg-sky-700 transition-all transform hover:scale-105"
          >
            <span className="bg-white text-sky-600 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold mr-3">
              {likedPropertyIds.length}
            </span>
            Compartir Selección
          </button>
        </div>
      )}

      {/* Modals */}
      {isRankingModalOpen && (
        <RankingModal 
          isOpen={isRankingModalOpen} 
          onClose={() => setIsRankingModalOpen(false)} 
          likedProperties={likedProperties} 
          onGenerateLink={handleGenerateSelectionLink} 
        />
      )}

      {isShareModalOpen && (
        <ShareGroupModal 
          isLoading={shareLoading} 
          sharedUrl={sharedUrl} 
          error={shareError} 
          onClose={() => setIsShareModalOpen(false)} 
        />
      )}

      {isFilterOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setIsFilterOpen(false)} />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[92%] max-w-md max-h-[85vh] bg-white p-6 rounded-2xl shadow-2xl overflow-y-auto animate-in zoom-in-95 duration-200">
            <LocationFilters 
              neighborhoods={neighborhoods}
              selectedNeighborhoods={selectedNeighborhoods}
              onNeighborhoodChange={setSelectedNeighborhoods}
              priceRange={priceRange}
              onPriceRangeChange={setPriceRange}
              onClear={() => { setPriceRange({ min: '', max: '' }); setSelectedNeighborhoods([]); }}
              onClose={() => setIsFilterOpen(false)}
              resultsCount={filteredProperties.length}
            />
          </div>
        </div>
      )}
    </div>
  );
};
